/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { JobVariationStatus } from '@gh/builder-shared-data';
import {
  AbstractSnapshotFormComponent,
  FormState,
  FormStateBuilder,
  FormStateSnapshot, getFormStateSnapshotValue,
  IS_REGION_AU_OR_NZ,
  whenAuOrNzRegion,
} from '@gh/common-shared';
import {
  DialogService,
  FormMode,
  isCreateFormMode,
  isEditableFormMode,
  isEditFormMode,
} from '@gh/core-ui';
import { isNotNil } from '@gh/core-util';
import { isNil } from 'lodash';
import {
  JobCoDetailsBottomInfo,
  JobCoDetailsDerivedInfo,
} from '../../../resources/job-co-details.model';

@Component({
  standalone: false,
  selector: 'gh-job-co-details-bottom-panel',
  templateUrl: 'job-co-details-bottom-panel.component.html',
  styleUrls: ['job-co-details-bottom-panel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class JobCoDetailsBottomPanelComponent extends AbstractSnapshotFormComponent<JobCoDetailsBottomInfo> {
  @Input() mode: FormMode;
  @Input() status: JobVariationStatus;
  @Input() producedDate: Maybe<Date>;
  @Input() printOnProcess: boolean;
  @Input() derivedInfo: JobCoDetailsDerivedInfo;

  @Output() approve = new EventEmitter<void>();
  @Output() reject = new EventEmitter<void>();
  @Output() printOnProcessChange = new EventEmitter<boolean>();

  isAuOrNzRegion = IS_REGION_AU_OR_NZ;

  constructor(private dialogs: DialogService,
              private formStateBuilder: FormStateBuilder) {
    super();
  }

  get editable(): boolean {
    return isCreateFormMode(this.mode)
      || isEditFormMode(this.mode) && this.status === JobVariationStatus.Quote && isNil(this.producedDate);
  }

  get isApproveVisible(): boolean {
    return isEditableFormMode(this.mode) && this.status === JobVariationStatus.Quote;
  }

  get canApprove(): boolean {
    return this.status === JobVariationStatus.Quote && isNotNil(this.producedDate);
  }

  get isRejectVisible(): boolean {
    return isEditableFormMode(this.mode) && this.status === JobVariationStatus.Quote;
  }

  get canReject(): boolean {
    return isEditFormMode(this.mode) && this.status === JobVariationStatus.Quote;
  }

  get isDateQuotePrinted(): boolean {
    return isEditableFormMode(this.mode) && isNotNil(this.producedDate);
  }

  get isPrintQuoteOnProcess(): boolean {
    return isEditableFormMode(this.mode) && this.status === JobVariationStatus.Quote && isNil(this.producedDate);
  }

  protected buildForm(snapshot: FormStateSnapshot<JobCoDetailsBottomInfo>): FormState<JobCoDetailsBottomInfo> {
    const bottomInfo = getFormStateSnapshotValue(snapshot);

    return this.formStateBuilder.buildPlain({
      snapshot,
      children: {
        taxRate: {
          required: [whenAuOrNzRegion(), bottomInfo.gstApplies],
          enabled: false,
        },
        gstApplies: void 0,
      },
    });
  }

}
